import { ColumnDef } from '@tanstack/react-table';
import constants from 'constants/constants';
import moment from 'moment-timezone';
import { getElementByName } from 'utilitys/helpers/general';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { ColoredDotCell } from 'components/common/Tables/Cells/ColoredDotCell/ColoredDotCell';
import { DynamicChipsCell } from 'components/common/Tables/Cells/DynamicChipsCell/DynamicChipsCell';
import { LatestUpdateCell } from 'components/common/Tables/Cells/LatestUpdateCell/LatestUpdateCell';
import { QualityScoreCell } from 'components/common/Tables/Cells/QualityScoreCell/QualityScoreCell';
import { RiskScoreCell } from 'components/common/Tables/Cells/RiskScoreCell/RiskScoreCell';
import { SPICell } from 'components/common/Tables/Cells/SPICell/SPICell';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';

import { FinishDateCell } from 'components/widgets/ProjectScorecards/components/FinishDateCell/FinishDateCell';
import { ProjectCell } from 'components/widgets/ProjectScorecards/components/ProjectCell/ProjectCell';
import { ScheduleAdherenceCell } from 'components/widgets/ProjectScorecards/components/ScheduleAdherenceCell/ScheduleAdherenceCell';
import { SubmittedCompletionDurationCell } from 'components/widgets/ProjectScorecards/components/SubmittedCompletionDurationCell/SubmittedCompletionDurationCell';

import { getColorFromStatus } from 'utilitys/Status/Status.utils';

export const serializeScoreCardsData = (data = {}) => {
    return Object.keys(data).reduce((accumulator, key) => {
        accumulator.push(
            data[key].reduce((itemAccumulator, item) => {
                itemAccumulator[item.name] = item.value;
                return itemAccumulator;
            }, {}),
        );
        return accumulator;
    }, []);
};

export const columnsV8: ColumnDef<any>[] = [
    {
        header: 'Project / Sub-program',
        ...columnsCommonConfig.projectNameSubProgram,
        accessorFn: (rowOriginal) => `${rowOriginal.project.name} / ${rowOriginal.contract.name}`,
        size: 300,
        cell: ({ row }) => {
            const original = row.original;
            const title = `${original.project.name} / ${original.contract.name}`;
            return <ProjectCell title={title} versionHashCode={original.version_hash_code?.toString()} />;
        },
        enableColumnFilter: true,
        meta: {
            filterPlaceholder: 'Search by name',
        },
    },
    {
        header: 'SPI',
        ...columnsCommonConfig.spi,
        accessorKey: 'spi.value',
        cell: ({ getValue }) => {
            return <SPICell value={getValue<number>()} />;
        },
    },
    {
        header: 'Planned (%)',
        ...columnsCommonConfig.criticalityWeightedPlanned,
        accessorKey: 'criticality_weighted_planned',
        cell: ({ getValue }) => `${getValue<string>()}%`,
    },
    {
        header: 'Actual (%)',
        ...columnsCommonConfig.criticalityWeightedActual,
        accessorKey: 'criticality_weighted_actual',
        cell: ({ getValue }) => `${getValue<string>()}%`,
    },
    {
        header: 'Actual Start',
        ...columnsCommonConfig.actualStartDate,
        accessorKey: 'actual_start_date',
        cell: ({ getValue }) => <div>{moment(getValue<number>()).format(constants.formats.date.default)}</div>,
    },
    {
        header: 'Finish Date',
        ...columnsCommonConfig.finishDate,
        accessorFn: (rowOriginal) => getElementByName(rowOriginal?.finish_date?.value, 'date').value,
        cell: ({ row }) => <FinishDateCell row={row} />,
    },
    {
        header: 'Baseline Finish',
        ...columnsCommonConfig.baselineFinishDate,
        accessorKey: 'baseline_finish_date',
        cell: ({ getValue }) => <div>{moment(getValue<number>()).format(constants.formats.date.default)}</div>,
    },
    {
        header: 'Quality Score',
        ...columnsCommonConfig.qualityNumeric,
        accessorKey: 'quality_numeric',
        cell: ({ getValue }) => {
            return <QualityScoreCell value={getValue<{ value: number }>().value} />;
        },
    },
    {
        header: 'Latest Update',
        ...columnsCommonConfig.latestVersion,
        accessorKey: 'last_version_date',
        cell: ({ row }) => {
            const original = row.original;
            const lastVersionDate = moment(original.last_version_date).format(constants.formats.date.default);
            const baselineVersionDate = moment(original.baseline_version_date).format(constants.formats.date.default);

            return <LatestUpdateCell baselineVersionDate={baselineVersionDate} lastVersionDate={lastVersionDate} />;
        },
    },
    {
        header: 'Program Tags',
        ...columnsCommonConfig.programTags,
        accessorFn: (rowOriginal) => rowOriginal?.tagList?.value.map((item) => item.value).join(','),
        cell: ({ row }) => {
            const list = row?.original?.tagList?.value.map((item) => item.value);
            return <DynamicChipsCell list={list || []} type={'tag'} />;
        },
    },
    {
        header: 'Risk Score',
        ...columnsCommonConfig.riskNumeric,
        accessorKey: 'risk_numeric',
        cell: ({ getValue }) => {
            return <RiskScoreCell value={getValue<{ value: number }>().value} />;
        },
    },
    {
        header: 'Progress',
        ...columnsCommonConfig.progress,
        accessorFn: (rowOriginal) => rowOriginal?.progress?.value.toFixed(2),
        cell: ({ row }) => {
            const progress = row.original.progress;
            return (
                <ColoredDotCell
                    color={getColorFromStatus(progress?.fluxIndex, 'Progress')}
                    toolTipContent={String(progress?.value)}
                />
            );
        },
    },
    {
        header: 'Risk',
        ...columnsCommonConfig.risk,
        accessorFn: (rowOriginal) => rowOriginal?.risk?.value.toFixed(2),
        cell: ({ row }) => {
            const risk = row.original.risk;
            return (
                <ColoredDotCell
                    color={getColorFromStatus(risk?.fluxIndex, 'Risk')}
                    toolTipContent={String(risk?.value)}
                />
            );
        },
    },
    {
        header: 'Quality',
        ...columnsCommonConfig.quality,
        accessorFn: (rowOriginal) => rowOriginal?.quality?.value.toFixed(2),
        cell: ({ row }) => {
            const quality = row.original.quality;

            return (
                <ColoredDotCell
                    color={getColorFromStatus(quality?.fluxIndex, 'Quality')}
                    toolTipContent={String(quality?.value)}
                />
            );
        },
    },
    {
        header: 'Submitted Completion (Total Duration)',
        ...columnsCommonConfig.submittedCompletionDate,
        accessorFn: (rowOriginal) => Number(rowOriginal?.submitted_completion?.value).toFixed(2),
        cell: ({ row }) => <SubmittedCompletionDurationCell row={row} />,
    },
    {
        header: 'Schedule Performance (Criticality Weighted)',
        ...columnsCommonConfig.criticalityAdherence,
        accessorFn: (rowOriginal) =>
            Number(getElementByName(rowOriginal?.criticality_adherence?.value || [], 'result').value).toFixed(2),
        cell: ({ row }) => {
            const criticalityAdherence = row.original.criticality_adherence;
            const fluxIndex = getElementByName(criticalityAdherence?.value || [], 'result').fluxIndex;
            return (
                <ScheduleAdherenceCell
                    value={criticalityAdherence?.value}
                    color={getColorFromStatus(fluxIndex, 'CriticalityAdherence')}
                />
            );
        },
    },
    {
        header: 'Schedule Performance (Completion Weighted)',
        ...columnsCommonConfig.milestoneAdherence,
        accessorFn: (rowOriginal) =>
            Number(getElementByName(rowOriginal?.milestone_adherence?.value || [], 'result').value).toFixed(2),
        cell: ({ row }) => {
            const milestoneAdherence = row.original.milestone_adherence;
            const fluxIndex = getElementByName(milestoneAdherence?.value || [], 'result').fluxIndex;
            return (
                <ScheduleAdherenceCell
                    value={milestoneAdherence?.value}
                    color={getColorFromStatus(fluxIndex, 'MilestoneAdherence')}
                />
            );
        },
    },
];

export const initialState: ITableColumnState = {
    sortBy: [{ id: 'projectNameSubProgram' }],
    hiddenColumns: [
        'riskNumeric',
        'progress',
        'risk',
        'quality',
        'submittedCompletionDate',
        'criticalityAdherence',
        'milestoneAdherence',
    ],
};
